import { useState, useEffect } from 'react';
import { Form, TextInput, Button, FileUploaderButton, Loading, FileUploaderItem } from 'carbon-components-react';
import styled from 'styled-components';
import { toaster } from '../../helpers/toaster';
import Select from "../../helpers/FixRequiredSelect";
import MyModal from '../../components/Modal/Modal';
import Label from '../../components/Label/Label';
import { selectStyles, formatReadOnlyStudyId } from '../../helpers/formUtils';
// api
import { getSimpleStudyList } from '../../api/studyListApi';
import { getLanguages } from '../../api/languages';
const AttachmentDetailModal = ({
	mode = "create",
	attachmentId = null,
	open = false,
	attachmentData = {
		id: "",
		name: "",
		url: "",
		type: "",
		attachment: null,
		study: "",
		language: "",
	},
	onClose = () => {},
	confirmSave,
	confirmUpdate,
	attachmentsLoading,
	setAttachmentsLoading,
	isStudyDetail,
  studyOrgStudyId,
  studyPgId,
  studyListOptions,
}) => {
	// create/edit
	const [modalMode, setModalMode] = useState("create");

	// component data
	const [attachmentInfo, setAttachmentInfo] = useState({});
	const [attachmentType, setAttachmentType] = useState({});
	const [attachmentTypeList, setAttachmentTypeList] = useState([
		{
			label: "(CSR) Clinical Study Report Synopsis",
			value: "CSR"
		},
		{
			label: "Results Plain Language Summary",
			value: "PLS"
		},
		{
			label: "Protocol Plain Language Summary",
			value: "PPLS"
		}
	]);
	const [attachmentFile, setAttachmentFile] = useState();

	// for study list dropdown
	const [studyList, setStudyList] = useState([]);
	const [language, setLanguage] = useState({})
	const [languageList, setLanguageList] = useState([]);
	// for when editing a study
	const [initialStudy, setInitialStudy] = useState({});

	// listen for modal "mode" changes create/edit/etc
	useEffect(() => setModalMode(mode), [mode]);

	// listen for attachmentData changes and make updates
	useEffect(() => {
		clearForm();

		// set attachment passed from props or default value
		setAttachmentInfo(attachmentData);

		// get initially selected item
		if (attachmentData && studyList && studyList.length) {
			let selectedStudy = studyList.find(item => item.value === attachmentData.study) || studyList[0];
			setInitialStudy(selectedStudy);
		}
		// setLanguage()
		if (attachmentData && languageList && languageList.length) {
			let selectedLanguage = languageList.find(item => item.label === attachmentData.language_name)
			setLanguage(selectedLanguage)
		}
		// set initial attachment type
		if (attachmentData && attachmentTypeList && attachmentTypeList.length) {
			let selectedType = attachmentTypeList.find(item => item.value === attachmentData.type) || attachmentTypeList[0];
			setAttachmentType(selectedType);
		}
	}, [attachmentData]);

	// load study list
	useEffect(() => {
		if (studyListOptions) {
			setStudyList(studyListOptions);
			setAttachmentsLoading(false)
		} else {
			getSimpleStudyList().then(res => {
				if (res && res.length) {
					let studies = res.map(item => ({
						value: item.id,
						label: item.org_study_id,
						}));
					setStudyList(studies);
				}
				setAttachmentsLoading(false)
			}).catch(error => {
				toaster("Failed to get simple study list", "error")
				setAttachmentsLoading(false)
			});
		}
		getLanguages().then(res => {
			if( res && res.length) {
				let languages = res.map(item => ({
					value: Number(item.id),
					label: item.name
				}))
				setLanguageList(languages)
			}
		})

	}, []);

	useEffect(() => {
		if(mode === 'create'){
			setAttachmentInfo({
				...attachmentInfo,
				study: studyPgId,
			})
		}
	}, [modalMode, studyPgId])

	// attachment handler
	const changeAttachmentInfo = (newData) => {
		setAttachmentInfo(newData);
	};

	// study handler
	const changeSelectedStudy = e => {
		setInitialStudy(e);
		changeAttachmentInfo({
			...attachmentInfo,
			study: e.value,
		});
	};

	// attachment type handler
	const changeAttachmentType = e => {
		setAttachmentType(e);
		changeAttachmentInfo({
			...attachmentInfo,
			type: e.value,
		});
	};

	const changeLanguage = e => {
		setLanguage(e);
		changeAttachmentInfo({
			...attachmentInfo,
			language: e.value,
		});
	};
	// garbage collection
	const clearForm = () => {
		const attachmentData = {
			id: "",
			name: "",
			url: "",
			type: "",
			attachment: null,
			study: "",
		}
		if(isStudyDetail){
			attachmentData.study = studyPgId
		}
		setAttachmentInfo(attachmentData);
		setAttachmentType({});
		setInitialStudy([]);
		setAttachmentFile(null);
	}

	// save api call
	const saveAttachment = (e) => {
		// form will handle most of validation, so button type="submit" thats why we preventDefault
		e.preventDefault();

		// stull that form doesn't validate
		if (!attachmentInfo || !attachmentInfo.name || !attachmentInfo.type) {
			return;
		}

		// start processing indicator
		setAttachmentsLoading(true);

		// create a proper js form object for file transfer
		let formData = new FormData();
		if(attachmentFile){
			formData.append('attachment', attachmentFile, attachmentFile.name);
		}
		// if (language) {
		// 	formData.append('language', language.value)
		// }
		formData.append('name', attachmentInfo.name);
		formData.append('type', attachmentInfo.type);
		formData.append('study', attachmentInfo.study);
		if(attachmentInfo.language){
			formData.append('language', attachmentInfo.language);
		}

		if (modalMode === "create") {
			confirmSave(formData).then((data) => {
				if (data) clearForm();
			})
		} else {
			confirmUpdate(formData, attachmentId)
		}
	};

	const addBtnStyle = {
		padding: "11px 18px",
    height: "auto",
    minHeight: "unset",
    borderRadius: "10px",
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "13.5px",
		width: "fit-content",
		marginLeft: "18px",
	};

	const additionalProps = {
		className: 'some-class',
		onSubmit: saveAttachment
	};

	return (
		<MyModal
			modalHeading="Attachment Details"
			size="sm"
			open={open}
			onRequestClose={()=>{
				onClose();
			}}
		>
			<AttachmentModalContent>

				{
					attachmentsLoading ? 
					<Loading/> 
					:
					<></>
				}
				
				<Form {...additionalProps}>

					<PartWrapper>
						<InputWrapper>
							{(mode === "create" && !isStudyDetail) ?
								<>
									<Label>Study ID*</Label>
									<Select
										id="studyList"
										options={studyList}
										value={initialStudy}
										onChange={e => changeSelectedStudy(e)}
										classNamePrefix="xogene"
										className="multi-select"
										styles={selectStyles()}
										disabled={attachmentsLoading}
										isSearchable
										required
									/>
								</>
								:
								// attachmentData?.org_study_id || "-"
								( mode === "create" && isStudyDetail) ? 
								formatReadOnlyStudyId( studyOrgStudyId || "-")
								: formatReadOnlyStudyId(attachmentData?.org_study_id || "-")
								

							}
						</InputWrapper>
					</PartWrapper>

					<PartWrapper>
						<Label>Attachment Type*</Label>
						<InputWrapper>
							<Select
								id="attachmentType"
								options={attachmentTypeList}
								value={attachmentType || {}}
								onChange={e => changeAttachmentType(e)}
								classNamePrefix="xogene"
								className="multi-select"
								styles={selectStyles()}
								isSearchable
								required
								disabled={attachmentsLoading}
							/>
						</InputWrapper>
					</PartWrapper>

					<PartWrapper>
						<InputWrapper>
							<TextInput
								value={attachmentInfo?.name || ""}
								labelText="Display Name*"
								// placeholder="Please enter Attachment Name"
								onChange={e => changeAttachmentInfo({
								...attachmentInfo,
								name: e.target.value,
								})}
								required
								disabled={attachmentsLoading}
							/>
						</InputWrapper>
					</PartWrapper>
					<PartWrapper>
						<InputWrapper>
						<Label>Language</Label>
							<Select
								id="attachmentLanguage"
								options={languageList}
								value={language}
								onChange={e => changeLanguage(e)}
								classNamePrefix="xogene"
								className="multi-select"
								styles={selectStyles()}
								isSearchable
								required
								disabled={attachmentsLoading}
								/>
						</InputWrapper>
					</PartWrapper>
					<PartWrapper>
						<Label>Attachment File*</Label>
						<FileUploadStyled>
							<PartWrapper>
							{mode !== "create" && 
							// <PaddingWrapper>
							<div style={{"marginBottom": "18px"}}>
								<span>Current File: </span>
								<a href={attachmentInfo?.attachment} target="_blank">{attachmentInfo?.attachment_info?.title}</a>
							</div>
							}
							<FileUploaderButton
								id="attachmentFile"
								disableLabelChanges={true}
								buttonKind="ghost"
								labelText="Select a file to attach"
								onChange={e => setAttachmentFile(e.target.files[0])}
								multiple={false}
								required
								disabled={attachmentsLoading}
								accept={['.pdf']}
							/>
							{attachmentFile && <FileUploaderItem
								name={attachmentFile?.name}
								status="edit"
								onDelete={e => setAttachmentFile(null)}
							/>}
							</PartWrapper>
						</FileUploadStyled>
					</PartWrapper>

					<ModalFooter>
						<Button 
							kind="secondary"
							onClick={onClose}
							style={addBtnStyle}
							disabled={attachmentsLoading}
						>Cancel</Button>

						<Button
							type="submit"
							kind="primary"
							// saveAttachment sent to form props
							style={addBtnStyle}
							disabled={(attachmentsLoading || (!attachmentFile && mode === 'create'))}
						>{ (modalMode === "create") ? "Create Attachment" : "Update Attachment" }</Button>
					</ModalFooter>

				</Form>

			</AttachmentModalContent>
		</MyModal>
	)
};

export default AttachmentDetailModal;

const AttachmentModalContent = styled.div`
	display: flex;
	flex-direction: column;
`

const PartWrapper = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: flex-start;
	margin-bottom: 18px;
`

const InputWrapper = styled.div`
	width: 100%;
`

const FileUploadStyled = styled(InputWrapper)`
  background-color: #f4f4f4;
	padding-left: 1rem;
	padding-bottom: 1rem;
	padding-top: 1rem;
  // button
  .bx--btn--ghost {
    color: black;
    border: 1px solid #8d8d8d;
    border-radius: 5px;
    background-color: #d9d9d9;
  }

  .bx--btn--ghost:hover, .bx--btn--ghost:active {
    color: #0f62fe;
    border-color: #0f62fe;
  }

  .bx--file-container {
    width: 100%;
  }

  .bx--file__selected-file {
    border-bottom: 1px dotted black;
    color: black;
  }
`

const ModalFooter = styled.div`
	width: 100%;
	display: flex;
	justify-content: flex-end;
`